import {
  getQueryString,
  handleError,
  page_size,
  createAxiosInstance,
} from 'shared/api/apiUtils';
import { refreshRoutesAndConnections } from 'shared/api/cacheRefresh';

export async function getDestinationsList(token: string, query: object) {
  const axiosInstance = await createAxiosInstance(token);
  const query_string = getQueryString(query);
  try {
    const resp = await axiosInstance.get(
      '/v2/integrations/?action_type=push&pager=limit&limit=' +
        page_size +
        '&' +
        query_string,
    );
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

// Every integration record the user can see, whatever role it plays: the Connections
// list. getDestinationsList keeps its action_type=push filter because the
// callers that ask for "destinations" (the flow map's picker, the availability count)
// really do mean records that can receive data.
export async function getConnectionsList(token: string, query: object) {
  const axiosInstance = await createAxiosInstance(token);
  const query_string = getQueryString(query);
  try {
    const resp = await axiosInstance.get(
      '/v2/integrations/?pager=limit&limit=' + page_size + '&' + query_string,
    );
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

// Returns how many destinations the user can see across all their workspaces
// (the API already scopes results to the user's visibility). Callers compare
// this against a Route's attached destinations to decide whether there
// is anything left to pick.
export async function countAvailableDestinations(token: string) {
  const resp = await getDestinationsList(token, {});
  // handleError swallows some failures (e.g. 401) and resolves undefined;
  // treat that as unknown rather than "no destinations" so callers fall
  // back to their safe path instead of misrouting to the create flow
  if (!resp) throw new Error('Failed to check destination availability');
  return resp.count;
}

// Every integration type, not just the ones that can receive data: a Connection
// may be created to send data (pull-only technologies), to receive it, or
// both, and the role is decided by the Routes it ends up in.
export async function getConnectionTypes(token: string, query?: object) {
  const axiosInstance = await createAxiosInstance(token);
  const query_string = getQueryString({ limit: page_size, ...(query || {}) });
  try {
    const resp = await axiosInstance.get(
      '/v2/integrations/types/?pager=limit&' + query_string,
    );
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function getDestinationTypes(token: string, query?: object) {
  const axiosInstance = await createAxiosInstance(token);
  const query_string = getQueryString({ limit: page_size, ...(query || {}) });
  try {
    const resp = await axiosInstance.get(
      '/v2/integrations/types/?action_type=push&pager=limit&' + query_string,
    );
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function getDestinationUrls(token: string, query?: object) {
  const axiosInstance = await createAxiosInstance(token);
  const query_string = getQueryString({ limit: page_size, ...(query || {}) });
  try {
    const resp = await axiosInstance.get(
      '/v2/integrations/urls/?action_type=push&pager=limit&' + query_string,
    );
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function addConnection(token: string, new_connection: object) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.post('/v2/integrations/', new_connection);
    refreshRoutesAndConnections();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function getConnectionDetails(
  token: string,
  integration_id: string,
) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.get('/v2/integrations/' + integration_id);
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}
